import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import $ from "jquery";
import "daterangepicker";
import "daterangepicker/daterangepicker.css";
import { Eye, EyeOff, File, X } from "lucide-react";
import CustomButton from "../../../components/controls/button/Button";
import CustomPopover from "../../../components/popover/Popover";
import IconInput from "../../../components/controls/input/iconInput/IconInput";
import UserSelect from "../../../components/userSelect/UserSelect";
import CheckboxInputStateSave from "../../../components/controls/checkboxStateSave/CheckboxStateSave";
import { ArrowDownMedium, SearchIcon, Cross, CalendarIcon, ColumnTripleIcon } from "../../../modules/default-svg";
import resourcesReportApi from "../../../store/services/resourcesReport.service";
import ResourceDutyRosterReportButton from "../../../components/Export/ResourceDutyRosterReportButton";
import { ParentType } from "../constants";
import styles from "../ResourcesReport.module.scss";

const ENTITY_TYPES = [
  { value: "client", label: "Client" },
  { value: "portfolio", label: "Portfolio" },
  { value: "program", label: "Program" },
  { value: "project", label: "Project" },
];

export default function ReportFilterBar({
  // Period
  summaryPeriod,
  onPeriodChange,
  selectedRange,
  customDateRange,
  onCustomDateRangeChange,
  periodOptions,
  // Entity filter
  showEntityFilters,
  entityFilter,
  onEntityFilterChange,
  availableEntityTypes,
  parentType,
  parentId,
  // Resource filter
  resourceFilter,
  onResourceFilterChange,
  userSelectRef,
  // Summary toggle
  showSummary,
  onToggleSummary,
  // Columns
  columns,
  onColumnStateChange,
  onColumnLabelChange,
  onResetColumns,
  // Title
  title,
  // Loading
  loading,
}) {
  // ─── Column popover state ──────────────────────────────────────────────
  const [columnPopoverOpen, setColumnPopoverOpen] = useState(false);

  // ─── Period dropdown state ──────────────────────────────────────────────
  const [openPeriodPopover, setOpenPeriodPopover] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStartInput, setCustomStartInput] = useState("");
  const [customEndInput, setCustomEndInput] = useState("");

  const customStartRef = useRef(null);
  const customEndRef = useRef(null);
  const customStartValRef = useRef("");
  const customEndValRef = useRef("");
  customStartValRef.current = customStartInput;
  customEndValRef.current = customEndInput;

  const settings = JSON.parse(localStorage.getItem("settings"));
  const dateFormat =
    settings?.regionalSettings?.dateFormat
      ?.replace(/dd/g, "DD")
      ?.replace(/yy/g, "YY")
      ?.replace(/mm/g, "MM") || "MM/DD/YYYY";

  // jQuery daterangepicker for custom dates
  useEffect(() => {
    if (!showCustomPicker) return;
    const timeout = setTimeout(() => {
      if (customStartRef.current) {
        $(customStartRef.current).daterangepicker(
          {
            singleDatePicker: true,
            startDate: customStartValRef.current ? moment(customStartValRef.current, "YYYY-MM-DD") : moment(),
            locale: { format: dateFormat },
            autoApply: true,
          },
          (chosen) => {
            const dateStr = chosen.format("YYYY-MM-DD");
            setCustomStartInput(dateStr);
            if (customEndValRef.current && dateStr > customEndValRef.current) {
              setCustomEndInput(dateStr);
              if (customEndRef.current) {
                try { $(customEndRef.current).data("daterangepicker")?.setStartDate(moment(dateStr)); } catch (e) { /* ignore */ }
              }
            }
          }
        );
      }
      if (customEndRef.current) {
        $(customEndRef.current).daterangepicker(
          {
            singleDatePicker: true,
            startDate: customEndValRef.current ? moment(customEndValRef.current, "YYYY-MM-DD") : moment(),
            locale: { format: dateFormat },
            autoApply: true,
          },
          (chosen) => {
            const dateStr = chosen.format("YYYY-MM-DD");
            setCustomEndInput(dateStr);
            if (customStartValRef.current && dateStr < customStartValRef.current) {
              setCustomStartInput(dateStr);
              if (customStartRef.current) {
                try { $(customStartRef.current).data("daterangepicker")?.setStartDate(moment(dateStr)); } catch (e) { /* ignore */ }
              }
            }
          }
        );
      }
    }, 50);
    return () => {
      clearTimeout(timeout);
      if (customStartRef.current) {
        try { $(customStartRef.current).data("daterangepicker")?.remove(); } catch (e) { /* ignore */ }
      }
      if (customEndRef.current) {
        try { $(customEndRef.current).data("daterangepicker")?.remove(); } catch (e) { /* ignore */ }
      }
    };
  }, [showCustomPicker, dateFormat]);

  const handleApplyCustom = () => {
    if (customStartInput && customEndInput && customStartInput <= customEndInput) {
      onPeriodChange("custom");
      onCustomDateRangeChange({ startDate: customStartInput, endDate: customEndInput });
      setOpenPeriodPopover(false);
      setShowCustomPicker(false);
    }
  };

  // ─── Entity filter dropdown ──────────────────────────────────────────────
  const [entityPopoverOpen, setEntityPopoverOpen] = useState(false);
  const [selectedEntityType, setSelectedEntityType] = useState(
    availableEntityTypes?.[0] || "client"
  );
  const [entitySearch, setEntitySearch] = useState("");
  const [entityItems, setEntityItems] = useState([]);
  const [entityHasNoRecords, setEntityHasNoRecords] = useState(false);
  const [entitySearching, setEntitySearching] = useState(false);

  // Visible entity type tabs based on parent
  const visibleEntityTypes = ENTITY_TYPES.filter((et) =>
    availableEntityTypes?.includes(et.value)
  );

  useEffect(() => {
    if (!entityPopoverOpen) return;
    let cancelled = false;
    setEntitySearching(true);
    const fetch = async () => {
      try {
        let res = [];
        const parentFilter = parentType && parentId ? { [`${parentType}Id`]: parentId } : {};
        switch (selectedEntityType) {
          case "client":
            res = await resourcesReportApi.searchClients(entitySearch);
            break;
          case "portfolio":
            res = await resourcesReportApi.searchPortfolios(entitySearch, parentFilter);
            break;
          case "program":
            res = await resourcesReportApi.searchPrograms(entitySearch, parentFilter);
            break;
          case "project":
            res = await resourcesReportApi.searchProjects(entitySearch, parentFilter);
            break;
          default:
            break;
        }
        if (!cancelled) {
          setEntityItems(res || []);
          // If the initial call (no search term) returns 0, there are no records at all
          if (!entitySearch) setEntityHasNoRecords((res || []).length === 0);
          setEntitySearching(false);
        }
      } catch {
        if (!cancelled) {
          setEntityItems([]);
          if (!entitySearch) setEntityHasNoRecords(true);
          setEntitySearching(false);
        }
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [entitySearch, entityPopoverOpen, selectedEntityType, parentType, parentId]);

  // ─── Period label ──────────────────────────────────────────────────────
  const periodLabel = (() => {
    const label = periodOptions.find((o) => o.value === summaryPeriod)?.label || "This week";
    if (!loading && selectedRange?.startDate && selectedRange?.endDate) {
      const range = `${moment(selectedRange.startDate).format("MMM D")} – ${moment(selectedRange.endDate).format("MMM D")}`;
      if (summaryPeriod === "custom") return range;
      return `${label} (${range})`;
    }
    return label;
  })();

  const entityFilterLabel = entityFilter
    ? `${ENTITY_TYPES.find((t) => t.value === entityFilter.type)?.label}: ${entityFilter.name}`
    : "Filter by";

  // ─── Duty roster report project scoping ──────────────────────────────────
  // Project overview already scopes everything to a single project – hide the
  // in-panel project selector entirely. Any other parent (client/portfolio/
  // program, or none) leaves it unset so the user must filter by project
  // inside the report panel itself (Generate stays disabled until they do),
  // with the project search itself narrowed down to that client/portfolio/program.
  const dutyRosterProjectId = parentType === ParentType.PROJECT ? parentId : undefined;
  const dutyRosterProjectFilters =
    parentType && parentType !== ParentType.PROJECT && parentId
      ? { [`${parentType}Id`]: parentId }
      : undefined;

  return (
    <div className={`${styles.header} ${loading ? styles.headerDisabled : ""}`}>
      <div className={styles.headerLeft}>
        {!parentType &&  <h2 className={styles.headerTitle}>{title}</h2>}
       

        {/* Entity filter dropdown */}
        {showEntityFilters && (
          <CustomPopover
            width="320px"
            icon={
              <CustomButton
                btnClass={`${styles.filterBtn} btn_combo btn_combo_white ${entityFilter ? styles.activeFilterBtn : ""}`}
                label={entityFilterLabel}
                clickHandler={() => {
                  setEntityPopoverOpen(!entityPopoverOpen);
                  setEntitySearch("");
                }}
                rightIcon={
                  <span className="d-inline-flex align-items-center">
                    {entityFilter && (
                      <span
                        className={styles.filterClearX}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntityFilterChange(null);
                        }}
                      >
                        <X size={14} />
                      </span>
                    )}
                    <ArrowDownMedium />
                  </span>
                }
              />
            }
            status={entityPopoverOpen}
          >
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center">
                <h6 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Filter by</h6>
                <div onClick={() => setEntityPopoverOpen(false)} className="cursor-pointer">
                  <Cross />
                </div>
              </div>

              {/* Entity type tabs */}
              <div className={styles.entityTypeTabs}>
                {visibleEntityTypes.map((et) => (
                  <button
                    key={et.value}
                    type="button"
                    className={`${styles.entityTypeTab} ${selectedEntityType === et.value ? styles.entityTypeTabActive : ""}`}
                    onClick={() => {
                      setSelectedEntityType(et.value);
                      setEntitySearch("");
                      setEntityHasNoRecords(false);
                    }}
                  >
                    {et.label}
                  </button>
                ))}
              </div>

              <IconInput icon={<SearchIcon />} placeholder={`Search ${selectedEntityType}s`} handleChange={setEntitySearch} />

              {entityFilter && (
                <div
                  className={styles.clearFilter}
                  onClick={() => {
                    onEntityFilterChange(null);
                    setEntityPopoverOpen(false);
                  }}
                >
                  Clear filter
                </div>
              )}

              <div style={{ maxHeight: "200px", overflow: "auto" }}>
                {entityHasNoRecords || (entityItems.length === 0 && entitySearch) ? (
                  <div className={styles.entityEmptyState}>
                    <File size={24} color="#342c82" strokeWidth={1.5} />
                    <span>No records found</span>
                  </div>
                ) : entityItems.length === 0 ? (
                  <div className={styles.entityEmptyState}>
                    <span style={{ color: "#8b8b8b", fontSize: 12 }}>Start typing to search</span>
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {entityItems
                      .filter((p) => !(entityFilter && entityFilter.type === selectedEntityType && p.id === entityFilter.id))
                      .map((p) => (
                        <span
                          key={p.id}
                          className={styles.filterItem}
                          onClick={() => {
                            onEntityFilterChange({
                              type: selectedEntityType,
                              id: p.id,
                              name: p.name,
                              client: p.client || null,
                            });
                            setEntityPopoverOpen(false);
                          }}
                        >
                          {p.name}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </CustomPopover>
        )}

        {/* Resource filter */}
        <div className={styles.resourceFilterWrapper}>
          <UserSelect
            multiple
            isAdminSearch
            refType={userSelectRef?.refType ?? null}
            refId={userSelectRef?.refId || null}
            value={resourceFilter}
            onChange={(users) => onResourceFilterChange(users || [])}
            buttonLabel="Filter Resources"
            searchPlaceholder="Search resources"
            selectedUsersDisplayMode="avatar-only"
            selectedUsersDisplayLimit={4}
          />
        </div>
      </div>

      <div className={styles.headerRight}>
        <ResourceDutyRosterReportButton
          projectId={dutyRosterProjectId}
          projectFilters={dutyRosterProjectFilters}
          resources={resourceFilter}
        />
        {/* Select Columns */}
        <CustomPopover
          icon={
            <CustomButton
              btnClass={`${styles.filterBtn} btn_combo btn_combo_white`}
              rightIcon={
                <span className="columnBadge ms-1">
                  {columns.filter((col) => col.isVisible).length}
                </span>
              }
              icon={<ColumnTripleIcon />}
              label="Select Columns"
              clickHandler={() => setColumnPopoverOpen(!columnPopoverOpen)}
            />
          }
          status={columnPopoverOpen}
          onOpenChange={(e, data) => setColumnPopoverOpen(data.open)}
        >
          <div className="d-flex flex-col gap-1">
            {columns.map((item) => (
              <CheckboxInputStateSave
                key={item.key}
                label={item.name}
                isColumnConfig={true}
                customName={item.customName}
                checked={item.isVisible}
                onChange={() => onColumnStateChange(item.name)}
                onLabelChange={(newLabel) => onColumnLabelChange(item.name, newLabel)}
              />
            ))}
            <div className="d-flex justify-content-end mt-2">
              <CustomButton
                label="Reset Columns"
                btnClass="d_btn d_btn_primary me-10"
                clickHandler={onResetColumns}
              />
              <CustomButton
                label="Cancel"
                btnClass="d_btn d_btn_smallWhite"
                clickHandler={() => setColumnPopoverOpen(false)}
              />
            </div>
          </div>
        </CustomPopover>

        {/* Period dropdown */}
        <div className={styles.periodDropdown}>
          <CustomPopover
            icon={
              <CustomButton
                label={periodLabel}
                btnClass={`${styles.periodDropdownOption} d_btn`}
                rightIcon={
                  <span className="ms-6 d-inline-flex">
                    <ArrowDownMedium />
                  </span>
                }
                clickHandler={() => {
                  setOpenPeriodPopover(!openPeriodPopover);
                  if (!openPeriodPopover) setShowCustomPicker(false);
                }}
              />
            }
            width={showCustomPicker ? "240px" : "140px"}
            PopSurfaceClass={styles.periodDropdownPopover}
            status={openPeriodPopover}
          >
            {periodOptions.map((option) => (
              <div
                key={option.value}
                className={styles.periodDropdownPopoverItem}
                onClick={() => {
                  if (option.value === "custom") {
                    setCustomStartInput(selectedRange?.startDate || "");
                    setCustomEndInput(selectedRange?.endDate || "");
                    setShowCustomPicker(true);
                  } else {
                    onPeriodChange(option.value);
                    setOpenPeriodPopover(null);
                    setShowCustomPicker(false);
                  }
                }}
              >
                {option.label}
              </div>
            ))}
            {showCustomPicker && (
              <div className={styles.customDatePicker}>
                <div className={styles.customDateInputRow}>
                  <label className={styles.customDateLabel}>From</label>
                  <div className={styles.customDateInputWrap}>
                    <CalendarIcon />
                    <input ref={customStartRef} type="text" readOnly className={styles.customDateInput} />
                  </div>
                </div>
                <div className={styles.customDateInputRow}>
                  <label className={styles.customDateLabel}>To</label>
                  <div className={styles.customDateInputWrap}>
                    <CalendarIcon />
                    <input ref={customEndRef} type="text" readOnly className={styles.customDateInput} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 4 }}>
                  <button
                    type="button"
                    style={{ fontSize: 11, padding: "3px 10px", border: "1px solid #d8d0f0", borderRadius: 4, background: "#fff", cursor: "pointer" }}
                    onClick={() => setShowCustomPicker(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{ fontSize: 11, padding: "3px 10px", border: "none", borderRadius: 4, background: "#342c82", color: "#fff", cursor: "pointer" }}
                    onClick={handleApplyCustom}
                    disabled={!customStartInput || !customEndInput || customStartInput > customEndInput}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </CustomPopover>
        </div>

        {/* Summary toggle */}
        <button
          type="button"
          className={`${styles.summaryToggleBtn} ${showSummary ? styles.summaryToggleBtnActive : ""}`}
          onClick={onToggleSummary}
          title={showSummary ? "Hide Summary" : "Show Summary"}
        >
          {showSummary ? <EyeOff size={14} /> : <Eye size={14} />}
          <span>{showSummary ? "Hide Summary" : "Show Summary"}</span>
        </button>
      </div>
    </div>
  );
}
